/**
 * ADR-0016 — Purchase soft-void status (prefer over hard delete).
 */

export function isPurchaseVoided(row) {
  if (!row || typeof row !== 'object') return false;
  if (row.voided === true) return true;
  const status = String(row.status || '')
    .trim()
    .toLowerCase();
  return (
    status === 'voided' ||
    status === 'cancelled' ||
    status === 'canceled' ||
    status === 'storno' ||
    status === 'annule' ||
    status === 'annulé'
  );
}

/** Active (stock-relevant) purchase line / header rows. */
export function isActivePurchaseRow(row) {
  if (!row || typeof row !== 'object') return false;
  if (row.isDraft === true || row.draft === true) return false;
  if (isPurchaseVoided(row)) return false;
  const status = String(row.status || '')
    .trim()
    .toLowerCase();
  if (status === 'draft' || status === 'brouillon') return false;
  return true;
}

/**
 * @param {object} line
 * @param {{ at?: string, correlationId?: string, actorId?: string, reason?: string }} [meta]
 */
export function markPurchaseLineVoided(line, meta = {}) {
  if (!line || typeof line !== 'object') return line;
  if (isPurchaseVoided(line)) return { ...line };
  const at = meta.at || new Date().toISOString();
  return {
    ...line,
    status: 'voided',
    voided: true,
    voidedAt: at,
    voidedBy: meta.actorId || line.voidedBy || null,
    voidReason: meta.reason || 'achat_void',
    inventoryCorrelationId: meta.correlationId || line.inventoryCorrelationId || null,
    previousStatus: line.status || line.previousStatus || null,
    updatedAt: at
  };
}

/**
 * Soft-void an unpaid supplier invoice linked to an achat (audit trail).
 * @param {object} invoice
 * @param {{ at?: string, purchaseId?: string, reason?: string }} [meta]
 */
export function markSupplierInvoiceVoided(invoice, meta = {}) {
  if (!invoice || typeof invoice !== 'object') return invoice;
  if (invoice.voided === true || String(invoice.status || '').toLowerCase() === 'voided') {
    return { ...invoice };
  }
  const at = meta.at || new Date().toISOString();
  return {
    ...invoice,
    status: 'voided',
    voided: true,
    voidedAt: at,
    voidedPurchaseId: meta.purchaseId != null ? String(meta.purchaseId) : invoice.voidedPurchaseId,
    voidReason: meta.reason || 'achat_void',
    cancelledAt: at,
    updatedAt: at
  };
}

export function isSupplierInvoiceVoided(invoice) {
  if (!invoice || typeof invoice !== 'object') return false;
  if (invoice.voided === true) return true;
  const status = String(invoice.status || '')
    .trim()
    .toLowerCase();
  return status === 'voided' || status === 'cancelled' || status === 'canceled';
}

/**
 * All lines of a receipt are soft-voided (UI hide).
 * @param {object[]} rows
 */
export function isPurchaseReceiptFullyVoided(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return false;
  return list.every((row) => isPurchaseVoided(row));
}

/**
 * Active inventory-relevant purchase rows for reports / analytics.
 * @param {object[]} purchases
 */
export function filterActivePurchases(purchases = []) {
  return (Array.isArray(purchases) ? purchases : []).filter(isActivePurchaseRow);
}
